import { Outlet } from "@tanstack/react-router";
import { memo, useCallback, useEffect, useRef, useState } from "react";

import Navbar from "~/components/@core/navbar";
import { SidebarMenuItem } from "./side-bar";
import ToggleButton from "~/components/pages/supervisor/users/toggle";
import { decodeJWToken } from "~/utils/basic";
import { decryptData } from "~/utils/crypto";
import {
  getAdminMenuItems,
  getAnalystMenuItems,
  getBusinessRulesMenuItems,
  getClientMenuItems,
  getSupervisorMenuItems,
} from "~/services/supervisor.services";
import { getWorklistTATFilters } from "~/services/supervisor.services";
import { ISidebarMenuItem } from "~/types/agent-types";
const SupervisorLayout = () => {
  const [isExpand, setIsExpand] = useState(false);
  const [userName, setUserName] = useState<string | null>(null);
  const [sidebarItemsToDisplay, setSidebarItemsToDisplay] = useState<
    ISidebarMenuItem[]
  >([]);

  const roleid = localStorage.getItem("roleid");

  async function fetchUserName() {
    const token = localStorage.getItem("id_token");
    if (token) {
      const payload = decodeJWToken(decryptData(token));
      setUserName(payload.user_name);
    }
  }

  // Fetch admin menu items once on mount
  async function fetchAdminMenu() {
    const { data, status } = await getAdminMenuItems("admin_menu_items");
    if (status && data && Array.isArray(data.data.menu_items)) {
      setSidebarItemsToDisplay(data.data.menu_items as ISidebarMenuItem[]);
    } else if (status && Array.isArray(data)) {
      setSidebarItemsToDisplay(data as ISidebarMenuItem[]);
    } else {
      setSidebarItemsToDisplay([]);
    }
  }
  // Fetch supervisor menu items once on mount
  async function fetchSupervisorMenu() {
    const { data, status } = await getSupervisorMenuItems(
      "supervisor_menu_items"
    );
    if (status && data && Array.isArray(data.data.menu_items)) {
      setSidebarItemsToDisplay(data.data.menu_items as ISidebarMenuItem[]);
    } else if (status && Array.isArray(data)) {
      setSidebarItemsToDisplay(data as ISidebarMenuItem[]);
    } else {
      setSidebarItemsToDisplay([]);
    }
  }

  const fetchCounterData = async () => {
    const { data: resData, status } = await getWorklistTATFilters();
    if (status) {
      sessionStorage.setItem("worklistTATFilters", JSON.stringify((resData as any)?.data ?? []));
    }
  };

  // Fetch client menu items once on mount
  async function fetchClientMenu() {
    const { data, status } = await getClientMenuItems("client_menu_items");
    if (status && data && Array.isArray(data.data.menu_items)) {
      setSidebarItemsToDisplay(data.data.menu_items as ISidebarMenuItem[]);
    } else if (status && Array.isArray(data)) {
      setSidebarItemsToDisplay(data as ISidebarMenuItem[]);
    } else {
      setSidebarItemsToDisplay([]);
    }
  }

  // Fetch business rule menu items once on mount
  async function fetchBusinessRuleMenu() {
    const { data, status } = await getBusinessRulesMenuItems(
      "business_rule_menu_items"
    );
    if (status && data && Array.isArray(data.data.menu_items)) {
      setSidebarItemsToDisplay(data.data.menu_items as ISidebarMenuItem[]);
    } else if (status && Array.isArray(data)) {
      setSidebarItemsToDisplay(data as ISidebarMenuItem[]);
    } else {
      setSidebarItemsToDisplay([]);
    }
  }

  async function fetchAnalystMenu() {
    const { data, status } = await getAnalystMenuItems(
      "analyst_rule_menu_items"
    );
    if (status && data && Array.isArray(data.data.menu_items)) {
      setSidebarItemsToDisplay(data.data.menu_items as ISidebarMenuItem[]);
    } else if (status && Array.isArray(data)) {
      setSidebarItemsToDisplay(data as ISidebarMenuItem[]);
    } else {
      setSidebarItemsToDisplay([]);
    }
  }

  useEffect(() => {
    fetchUserName();
  }, []);

  //load dynamic menu as per roleid
  useEffect(() => {
    if (!roleid) {
      setSidebarItemsToDisplay([]);
      return;
    }

    switch (roleid) {
      case "12":
        fetchAdminMenu();
        break;
      case "3":
        fetchSupervisorMenu();
        fetchCounterData();
        break;
      case "8":
        fetchClientMenu();
        break;
      case "16":
        fetchBusinessRuleMenu();
        break;
      case "18":
        fetchAnalystMenu();
        break;
      default:
        setSidebarItemsToDisplay([]);
        console.log("No menu to fetch for this role");
    }
  }, [roleid]);

  const sidebarRef = useRef<HTMLDivElement>(null);

  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (
      sidebarRef.current &&
      !sidebarRef.current.contains(event.target as Node)
    ) {
      setIsExpand(false);
    }
  }, []);

  useEffect(() => {
    if (isExpand) {
      document.addEventListener("click", handleClickOutside);
    } else {
      document.removeEventListener("click", handleClickOutside);
    }

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [handleClickOutside, isExpand]);

  return (
    <div className="flex flex-col h-screen overflow-hidden">
      <Navbar userName={userName} />
      <div className="flex flex-grow overflow-scroll">
        <aside className="space-y-2 bg-neutral-100" ref={sidebarRef}>
          <ToggleButton isExpand={isExpand} setIsExpand={setIsExpand} />
          {isExpand && (
            <div className="w-52 border-r h-full">
              <div>
                <ul className="w-full divide-y flex flex-col">
                  {sidebarItemsToDisplay.length === 0 ? (
                    <li className="p-4 text-gray-400">No menu items</li>
                  ) : (
                    sidebarItemsToDisplay.map(
                      (item: ISidebarMenuItem, idx: number) => (
                        <SidebarMenuItem
                          key={idx}
                          item={item}
                          setIsExpand={setIsExpand}
                        />
                      )
                    )
                  )}
                </ul>
              </div>
            </div>
          )}
        </aside>
        <main className="p-4 flex-grow overflow-y-scroll">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default memo(SupervisorLayout);
